import {erIkkeTomStreng, erTomEllerNil} from "felles/stoettefunk/strenger";
import {erIkkeTomListe, erTomListe, første} from "felles/stoettefunk/lister";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('aksjenummer');

export function intervallerSomStreng(intervaller: Intervall | Intervall[]) {
    if (erNil(intervaller)) return null;
    if (!Array.isArray(intervaller)) intervaller = [intervaller];
    return intervaller
      .map(intervall => {
          if (intervall.start == intervall.slutt) return `${intervall.start}`;
          return `${intervall.start}-${intervall.slutt}`
      })
      .join(', ')
}

/**
 * regexp som definerer gyldig aksjenummer streng-representasjon.
 * Skrevet på mer forståelig vis: "grupper av <tall|tall-tall> separert med ','"
 * der 'tall' er et heltall uten ledende 0
 * leddet '(,(?=\d+)|$)' - en regepx lookahead - sikrer at ',' må være etterfulgt av et tall
 * MERK: denne må holdes i synk med tilsvarende på java-siden no.systemfabrikken.lederplan.domene.aksjeeierbok.AksjenummerSamling.AKSJENUMMER_STRENGFORMAT
 */
const gyldigIntervalstrengUttrykk = /^(\s*([1-9]\d*|([1-9]\d*\s*-\s*[1-9]\d*))\s*(,\s*(?=\d+)|$)\s*)+$/;

/** Sann dersom angitt streng ikke er tom/null og innholder kommeseparerte liste med intevaller ('start' - 'slutt') og/eller enkeltaksjer.
 * Eksempler:
 * <pre>
 *     1,2
 *     1-5,7,8
 * </pre>
 * Se tester for ytterligere eksempler.
 */
export function erGyldigIntervallerstreng(intervallerStr: string): boolean {
    return gyldigIntervalstrengUttrykk.test(intervallerStr);
}

/** Deserialiserer streng-representasjon av aksjenummer til en liste med {@link Intervall} instanser.
 * Returnerte instanser er sortert i stigende rekkefølge etter {@link Intervall#start} */
export function dekodIntervallerStreng(intervallerStr: string | null): Intervall[] {
    if (erTomEllerNil(intervallerStr)) return [];
    return intervallerStr!
      .split(',')
      .filter(erIkkeTomStreng)
      .map(dekodIntervallStreng)
      .sort(sorter);
}

function dekodIntervallStreng(intervallStr: string): Intervall {
    const [startStr, sluttStr] = intervallStr.split('-')
    const start = Number.parseInt(startStr);
    const slutt = sluttStr ? Number.parseInt(sluttStr) : start;
    return {start, slutt};
}

/** returnerer liste med intevaller som er negative, dvs der start er større ennn slutt */
export function inneholderNegative(intervaller: Intervall[]): Intervall[] {
    return intervaller.filter(intervall => intervall.start > intervall.slutt);
}

/** returnerer liste med informasjon om overlappende intervaller */
export function inneholderOverlapper(intervaller: Intervall[]): { intervall: Intervall, overlapperMed: Intervall[] }[] {
    //lag map med ett innslag per intervall, der verdi er en liste andre intervaller dette intervallet overlapper med
    const overlapper = intervaller.reduce((overlapper, intervall, indeks, intervaller) => {
        return overlapper.set(
          intervall,
          finnOverlappendeI(intervall, intervaller)
            //ignorer overlapp dersom det allerede er reg en overlapp på mellom dette og intervall
            .filter(overlapp => !overlapper.get(overlapp)?.includes(intervall)))
    }, new Map<Intervall, Intervall[]>)

    //transformer mapet til en liste med {intervall, overlapperMed} innslag
    return [...overlapper.entries()]
      .map(innslag => ({intervall: innslag[0], overlapperMed: innslag[1]}))
      //fjerner innslag som ikke representerer noen overlapp
      .filter(innslagMedOverlapp => erIkkeTomListe(innslagMedOverlapp.overlapperMed))
}

/** returnerer liste med innslag i intervaller som overlapper angitt intervall */
export function finnOverlappendeI(intervall: Intervall, intervaller: Intervall[]) {
    return intervaller.filter((kandidat) =>
      kandidat !== intervall && //ignorer intervallet vi ser på
      erOverlapp(intervall, kandidat)); //sjekk om kandidat og intervall overlapper
}

/** sann dersom a og b overlapper */
export function erOverlapp(a: Intervall, b: Intervall): boolean {
    if (b.start >= a.start && b.start <= a.slutt) return true;
    if (b.slutt >= a.start && b.slutt <= a.slutt) return true;
    if (b.start <= a.start && b.slutt >= a.slutt) return true;
    return false;
}

/** sann dersom det ikke er mellomrom mellom a og b */
export function erUntenMellomrom(a: Intervall, b: Intervall): boolean {
    if (a.slutt < b.start - 1) return false;
    if (b.slutt < a.start - 1) return false;
    return true;
}

/** returnerer liste med informasjon om forekomster i a som overlapper med forekomster i b. */
export function finnOverlappendeMellom(a: Intervall[], b: Intervall[]): { a: Intervall; overlapper_b: Intervall[] }[] {
    //for hver a, finn mulige overlapp i b
    return a.map(a_forekomst => ({
        a: a_forekomst,
        overlapper_b: b.filter(b_forekomst => erOverlapp(a_forekomst, b_forekomst))
    }))
      //fjern a-er som ikke har overlapp i b
      .filter(a_forekomst => erIkkeTomListe(a_forekomst.overlapper_b));
}

/** returnerer en liste med intervaller som en normalisert representasjon av angitt intervallerStr.
 * Dvs det minste mulige settet av intervaller som representerer angitt intervallerStr.
 * Returnert samling er sortert etter intervall.start i stigende rekkefølge */
export function normaliserIntervallerStr(intervallerStr: string | null): Intervall[] {
    return normaliserIntervaller(dekodIntervallerStreng(intervallerStr));
}

/** returnerer en liste med intervaller som en normalisert representasjon av angitt intervaller.
 * Dvs det minste mulige settet av intervaller som representerer angitt intervallerStr.
 * Returnert samling er sortert etter intervall.start i stigende rekkefølge */
export function normaliserIntervaller(intervaller: Intervall[]): Intervall[] {
    return intervaller
      .sort(sorter)
      .reduce((normalisert, neste) => {
          if (erTomListe(normalisert)) normalisert.push({...neste}); //lag en kopi - denne muteres
          const forrige = normalisert[normalisert.length - 1];
          if (erOverlapp(forrige, neste) || erUntenMellomrom(forrige, neste)) {
              forrige.start = Math.min(forrige.start, neste.start);
              forrige.slutt = Math.max(forrige.slutt, neste.slutt);
          } else
              normalisert.push({...neste}); //lag en kopi - denne muteres
          return normalisert;
      }, [] as {start:number; slutt:number;}[] );
}

/** returnerer en liste med intervaller som en representerer hullene i angitt intervallerStr.
 * En samling har ikke hull dersom unionen av angitte intervaller starter på 1 og er uten brudd opp til høyste slutt-verdi.
 * Returnert samling er sortert etter intervall.start i stigende rekkefølge. */
export function hullIIntervallerStr(intervallerStr: string): Intervall[] {
    return hullIIntervaller(dekodIntervallerStreng(intervallerStr));
}

/** Returnerer en liste med intervaller som en representerer hullene i angitt intervaller.
 * En samling har ikke hull dersom unionen av angitte intervaller starter på 1 og er uten brudd opp til høyste slutt-verdi.
 * Returnert samling er sortert etter intervall.start i stigende rekkefølge. */
export function hullIIntervaller(intervaller: Intervall[]): Intervall[] {
    if (erTomListe(intervaller)) return [];
    const normaliserte = normaliserIntervaller(intervaller);
    const hull = [];

    //aksjenummer må starte på 1
    if(første(normaliserte)!.start > 1)
        hull.push({start: 1, slutt: første(normaliserte)!.start - 1})

    for (let i = 0; i < normaliserte.length - 1; i++)
        hull.push({start: normaliserte[i].slutt + 1, slutt: normaliserte[i + 1].start - 1})

    return hull;
}


/** returnerer summen av intervallene i angitt intervallerStr. */
export function summerIntervallerStr(intervallerStr: string): number {
    return summerIntervaller(dekodIntervallerStreng(intervallerStr))
}

/** returnerer summen av intervallene. */
export function summerIntervaller(intervaller: Intervall[]): number {
    return intervaller.reduce((a, b) => {
        return a + (b.slutt - b.start + 1)
    }, 0)
}

function sorter(a: Intervall, b: Intervall) {
    return a.start - b.start;
}

export interface Intervall {
    readonly start: number;
    readonly slutt: number;
}
